import React, { useEffect, useState } from "react";
import Loader from "../../../common/loader";
import EmployeeCardView from "./employeeCardView";
import EmployeeTableView from "./employeeTableView";
import AddEmployeeModal from "./addEmployee";
import InviteEmployeeDialog from "./inviteEmployee";
import {
  IconButton,
  Button,
  Paper,
  InputBase,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAddAlt as PersonAddAltIcon,
  Email as EmailIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  getEmployees,
  deleteEmployeeById,
  blockEmployee,
} from "../../../services/employee.service";
import { Business, Employee } from "../../../validation/business.validation";
import { getLoginUserData } from "../../../services/account.service";

const defaultBusiness: Business = {
  name: "",
  ein: "",
  email: "",
  phone: "",
  address: "",
  synonyms: "",
  website: "",
  id: "",
};

const Employees: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<"grid" | "table">("grid");
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState({ page: 1, limit: 10, search: "", businessId: "" });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [openModal, setOpenModal] = useState(false);
  const [user, setUser] = React.useState<Business>(defaultBusiness);
  const [editEmployeeDetails, setEditEmployeeDetails] = React.useState<Employee | null>(null);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const user = await getLoginUserData();
        if (user?.id) {
          setUser(user);
          const updatedQuery = { ...query, businessId: user?.businessId ? user?.businessId : user.id  };
          setQuery(updatedQuery);
          await fetchEmployees(updatedQuery);
        }
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load co-owners.");
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.businessId) fetchEmployees(query);
    }, 700);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.page]);

  const fetchEmployees = async (payload: any) => {
    setIsLoading(true);
    try {
      const res = await getEmployees(payload);
      setEmployees(res.data);
      setPaginationData({ page: res.page, limit: res.limit, total: res.total });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch employees.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEmployee = async (employee: any) => {
    setIsLoading(true);
    try {
      await deleteEmployeeById(employee.id);
      showSnackbar("success", "Employee deleted successfully.");
      fetchEmployees(query);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete employee.");
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateEmployee = async (employee: any) => {
    setIsLoading(true);
    try {
      await blockEmployee(employee.id, { isActive: !employee.isActive });
      showSnackbar(
        "success",
        `Employee ${
          employee.isActive ? "deactivated" : "activated"
        } successfully.`
      );
      fetchEmployees(query);
    } catch (error: any) {
      showSnackbar(
        "error",
        error?.message || "Failed to update employee status."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const editEmployee = async (employee: Employee) => {
    if (employee) {
      setOpenModal(true);
      setEditEmployeeDetails(employee);
    }
  };

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prev) => ({ ...prev, search: event.target.value }));
  };

  const handlePageChange = (_: any, value: number) => {
    setQuery((prev) => ({ ...prev, page: value }));
  };

  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 py-2 px-4">
          <Paper
            component="form"
            className="flex items-center border border-gray-300 rounded-full px-2"
            elevation={0}
            sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              minWidth: { xs: '100%', sm: '350px' },
              maxWidth: { xs: '100%', sm: '450px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <SearchIcon className="text-gray-400 ml-2" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search employee"
              value={query.search}
              onChange={handleSearchChange}
            />
          </Paper>
          <div className="flex items-center flex-wrap gap-3">
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Add Employee
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setOpenInviteDialog(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Invite Employee
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
              <IconButton
                onClick={() => setView("grid")}
                className={`!p-2 ${view === "grid" ? "bg-gray-100" : ""}`}
              >
                <ViewModuleIcon
                  className={view === "grid" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
              <IconButton
                onClick={() => setView("table")}
                className={`!p-2 ${view === "table" ? "bg-gray-100" : ""}`}
              >
                <ViewListIcon
                  className={view === "table" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
            </div>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {employees.length === 0 ? (
              <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-pulse">
                    <SearchRoundedIcon
                      className="text-gray-400"
                      sx={{ fontSize: 48 }}
                    />
                  </div>
                </div>
                <Typography variant="h6" className="mt-4 text-gray-700">
                  No Employees Found
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  You can try adjusting your search or add a new employee to get
                  started.
                </Typography>
              </Box>
            ) : view === "grid" ? (
              <EmployeeCardView
                employees={employees}
                onEdit={editEmployee}
                onDelete={deleteEmployee}
                onBlock={deactivateEmployee}
              />
            ) : (
              <EmployeeTableView
                employees={employees}
                onEdit={editEmployee}
                onDelete={deleteEmployee}
                onBlock={deactivateEmployee}
              />
            )}
            {employees.length > 0 && (
              <div className="flex flex-col md:flex-row justify-between md:items-center items-center px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm gap-2">
                <Pagination
                  count={totalPages}
                  page={paginationData.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="medium"
                />
                <div className="text-sm text-gray-600 text-center md:text-left">
                  Showing {(paginationData.page - 1) * paginationData.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    paginationData.page * paginationData.limit,
                    paginationData.total
                  )}{" "}
                  of {paginationData.total} results
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AddEmployeeModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setEditEmployeeDetails(null);
          fetchEmployees(query);
        }}
        businessId={query.businessId}
        editMode={!!editEmployeeDetails}
        employeeData={editEmployeeDetails || undefined}
      />
      <InviteEmployeeDialog
        open={openInviteDialog}
        onClose={() => setOpenInviteDialog(false)}
        user={user}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Employees;
